import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  TooltipProps
} from 'recharts';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, isSameMonth } from 'date-fns';
import { formatCurrency } from '../../lib/utils';
import type { CampaignPhase, CampaignStatus } from '../../lib/types';
import { getCampaignStartDate } from '../../lib/campaignDuration';

interface FundraisingChartProps {
  phase: CampaignPhase;
  status: CampaignStatus;
  electionDate: Date;
  office?: string;
  prediction?: any;
}

interface DataPoint {
  fullDate: Date;
  date: string;
  monthlyFundraising: number;
  cumulativeFundraising: number;
  monthlySpending: number;
  cumulativeSpending: number;
  polling: number | null;
  monthlyFundraisingPercent: string;
  cumulativeFundraisingPercent: string;
  monthlySpendingPercent: string;
  cumulativeSpendingPercent: string;
  phase: 'initial' | 'mid' | 'final';
}

const TEN_MONTH_SPENDING_STRATEGY = [
  0.001, // month 1 (Initial)
  0.002, // month 2 (Initial) 
  0.009, // month 3 (Initial)
  0.02, // month 4 (Mid)
  0.05, // month 5 (Mid)
  0.05, // month 6 (Mid)
  0.08, // month 7 (Mid)
  0.12, // month 8 (Final)
  0.28, // month 9 (Final)
  0.32  // month 10 (Final)
];

function getExponentialGrowth(index: number, total: number, exponent: number = 2.5): number {
  const progress = index / (total - 1);
  return Math.pow(progress, exponent);
}

function getFundraisingExponent(office: string, phase: CampaignPhase): number {
  const officeType = office.toLowerCase();
  
  if (officeType.includes('board') || officeType.includes('trustee') || officeType.includes('commissioner')) {
    return phase === 'primary' ? 1.8 : 2.0; // Gentler curve for hyper-local
  }
  
  if (officeType.includes('council') || officeType.includes('mayor')) {
    return phase === 'primary' ? 2.0 : 2.2; // Moderate curve for local
  }
  
  if (officeType.includes('county')) {
    return phase === 'primary' ? 2.2 : 2.4; // Steeper for county
  }
  
  if (officeType.includes('state') || officeType.includes('governor')) {
    return phase === 'primary' ? 2.4 : 2.6; // Even steeper for state
  }
  
  if (officeType.includes('congress') || officeType.includes('senate') || officeType.includes('representative')) {
    return phase === 'primary' ? 2.6 : 2.8; // Steepest for federal
  }
  
  return 2.5; // Default exponent
}

function lerp(start: number, end: number, t: number): number {
  return start * (1 - t) + end * t;
}

function normalize(arr: number[]): number[] {
  const sum = arr.reduce((acc, val) => acc + val, 0);
  return arr.map(val => val / sum);
}

function getScaledDistribution(realMonthsTotal: number, baseStrategy: number[]): number[] {
  if (realMonthsTotal === 10) return baseStrategy;

  const result: number[] = [];
  const baseLength = baseStrategy.length;

  if (realMonthsTotal < baseLength) {
    // Compress the strategy
    const ratio = (baseLength - 1) / (realMonthsTotal - 1);
    for (let i = 0; i < realMonthsTotal; i++) {
      const baseIndex = Math.min(Math.floor(i * ratio), baseLength - 1);
      const nextIndex = Math.min(baseIndex + 1, baseLength - 1);
      const fraction = (i * ratio) - baseIndex;
      result.push(lerp(baseStrategy[baseIndex], baseStrategy[nextIndex], fraction));
    }
  } else {
    // Expand the strategy
    const ratio = (baseLength - 1) / (realMonthsTotal - 1);
    for (let i = 0; i < realMonthsTotal; i++) {
      const position = i * ratio;
      const baseIndex = Math.floor(position);
      const nextIndex = Math.min(baseIndex + 1, baseLength - 1);
      const fraction = position - baseIndex;
      result.push(lerp(baseStrategy[baseIndex], baseStrategy[nextIndex], fraction));
    }
  }

  return normalize(result);
}

function getPhaseForMonth(monthIndex: number, totalMonths: number): 'initial' | 'mid' | 'final' {
  const progress = monthIndex / (totalMonths - 1);
  if (progress < 0.3) return 'initial';
  if (progress < 0.7) return 'mid';
  return 'final';
}

export function FundraisingChart({ phase, status, electionDate, office = '', prediction }: FundraisingChartProps) {
  const targetAmount = prediction?.recommendedBudget || 585000;

  const data = useMemo(() => {
    const officeType = office.toLowerCase();
    let electionType = phase === 'primary' ? 'primary' : 'general-even';
    
    if (officeType.includes('board') || officeType.includes('trustee') || officeType.includes('commissioner')) {
      electionType = phase === 'primary' ? 'primary' : 'general-odd';
    }
    
    const campaignStart = getCampaignStartDate(electionDate, officeType, electionType);
    const months = eachMonthOfInterval({
      start: startOfMonth(campaignStart),
      end: endOfMonth(electionDate)
    });

    // Filter out duplicate months
    const uniqueMonths = months.filter((month, index, array) => 
      array.findIndex(m => isSameMonth(m, month)) === index
    );

    const totalMonths = uniqueMonths.length;
    const spendingDistribution = getScaledDistribution(totalMonths, TEN_MONTH_SPENDING_STRATEGY);
    
    const exponent = getFundraisingExponent(office, phase);
    const rawFundraisingDistribution = Array.from({ length: totalMonths }, (_, i) => 
      getExponentialGrowth(i, totalMonths, exponent)
    );
    const fundraisingDistribution = normalize(rawFundraisingDistribution);

    let cumulativeFundraising = 0;
    let cumulativeSpending = 0;

    return uniqueMonths.map((month, index) => {
      const monthlySpending = Math.round(spendingDistribution[index] * targetAmount);
      const monthlyFundraising = Math.round(fundraisingDistribution[index] * targetAmount);

      cumulativeSpending += monthlySpending;
      cumulativeFundraising += monthlyFundraising;

      // Add polling data points every 2 months for more consistent spacing
      const polling = index % 2 === 0 ? 45 + (Math.random() * 10 - 5) : null;

      // Format date consistently
      const dateFormat = 'MMM yyyy';

      return {
        fullDate: month,
        date: format(month, dateFormat),
        monthlyFundraising,
        cumulativeFundraising,
        monthlySpending,
        cumulativeSpending,
        polling,
        monthlyFundraisingPercent: `${((monthlyFundraising / targetAmount) * 100).toFixed(1)}%`,
        cumulativeFundraisingPercent: `${((cumulativeFundraising / targetAmount) * 100).toFixed(1)}%`,
        monthlySpendingPercent: `${((monthlySpending / targetAmount) * 100).toFixed(1)}%`,
        cumulativeSpendingPercent: `${((cumulativeSpending / targetAmount) * 100).toFixed(1)}%`,
        phase: getPhaseForMonth(index, totalMonths)
      };
    });
  }, [electionDate, phase, status, office, targetAmount, prediction]);

  const today = format(new Date(), 'MMM yyyy');

  const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (!active || !payload || !payload[0]) return null;

    const monthData = payload[0].payload as DataPoint;
    const phaseLabels = {
      initial: 'Initial Phase',
      mid: 'Mid-Campaign',
      final: 'Final Push'
    };

    return (
      <div className="bg-[#1a2236] p-4 border border-white/10 rounded-lg shadow-xl">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">{label}</p>
          <span className="text-xs px-2 py-1 rounded bg-white/10 text-[#026fb9]">
            {phaseLabels[monthData.phase]}
          </span>
        </div>
        <div className="space-y-2">
          <div className="space-y-1">
            <p className="text-xs text-gray-400">Monthly Activity</p>
            <p className="text-sm text-[#026fb9]">
              Fundraising: {formatCurrency(monthData.monthlyFundraising)} ({monthData.monthlyFundraisingPercent})
            </p>
            <p className="text-sm text-[#edbf06]">
              Spending: {formatCurrency(monthData.monthlySpending)} ({monthData.monthlySpendingPercent})
            </p>
          </div>
          
          <div className="pt-2 border-t border-white/10 space-y-1">
            <p className="text-xs text-gray-400">Campaign Total</p>
            <p className="text-sm text-[#026fb9]">
              Total Raised: {formatCurrency(monthData.cumulativeFundraising)} ({monthData.cumulativeFundraisingPercent})
            </p>
            <p className="text-sm text-[#edbf06]">
              Total Spent: {formatCurrency(monthData.cumulativeSpending)} ({monthData.cumulativeSpendingPercent})
            </p>
          </div>

          {monthData.polling && (
            <div className="pt-2 border-t border-white/10">
              <p className="text-sm text-red-500">
                Polling: {monthData.polling.toFixed(1)}%
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Fundraising & Spending Strategy</h2>
          <p className="text-sm text-gray-400">Projected monthly campaign finance activity</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-400">Target Budget</div>
          <div className="text-xl font-semibold text-[#edbf06]">{formatCurrency(targetAmount)}</div>
        </div>
      </div>

      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="rgba(255,255,255,0.1)"
              horizontal={true}
              vertical={true}
            />
            <XAxis
              dataKey="date"
              stroke="rgba(255,255,255,0.5)"
              tick={{ 
                fill: 'rgba(255,255,255,0.5)',
                fontSize: 10
              }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={60}
              tickMargin={15}
            />
            <YAxis
              stroke="rgba(255,255,255,0.5)"
              tick={{ fill: 'rgba(255,255,255,0.5)' }}
              tickFormatter={(value) => `$${(value / 1000)}k`}
              domain={[0, 'auto']}
              padding={{ top: 20 }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            <ReferenceLine
              x={today}
              stroke="#026fb9"
              strokeDasharray="3 3"
              label={{
                value: "Today",
                position: "top",
                fill: "#026fb9",
                fontSize: 10
              }}
            />

            <Line
              type="monotone"
              name="Monthly Fundraising"
              dataKey="monthlyFundraising"
              stroke="#026fb9"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 8 }}
            />
            <Line
              type="monotone"
              name="Monthly Spending"
              dataKey="monthlySpending"
              stroke="#edbf06"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{ r: 8 }}
            />
            <Line
              type="monotone"
              name="Polling"
              dataKey="polling"
              stroke="#ef4444"
              strokeWidth={0}
              dot={{ r: 4, fill: "#ef4444" }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="flex justify-center gap-8">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#026fb9] rounded-full" />
          <span className="text-sm text-gray-400">Monthly Fundraising</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#edbf06] rounded-full" />
          <span className="text-sm text-gray-400">Monthly Spending</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#ef4444] rounded-full" />
          <span className="text-sm text-gray-400">Polling</span>
        </div>
      </div>
    </div>
  );
}